import type { DigitallySigned } from '../tls/vectors';

export type EntryType = 'x509' | 'precert';

export const LogEntryType = { x509_entry: 0, precert_entry: 1 } as const;
export const SignatureType = { certificate_timestamp: 0, tree_hash: 1 } as const;
export const CT_V1 = 0;
export const MerkleLeafType = { timestamped_entry: 0 } as const;

/** RFC 6962 §3.4 TimestampedEntry. */
export interface TimestampedEntry {
  /** milliseconds since the Unix epoch */
  timestamp: number;
  entryType: EntryType;
  /** DER leaf certificate (x509 entries) */
  x509?: Uint8Array;
  /** Precert: SHA-256 of the issuer's SubjectPublicKeyInfo plus the poison-free TBSCertificate. */
  precert?: { issuerKeyHash: Uint8Array; tbs: Uint8Array };
  extensions: Uint8Array;
}

/** RFC 6962 §3.4 MerkleTreeLeaf (the `leaf_input` of get-entries). */
export interface MerkleTreeLeaf {
  version: number;
  leafType: number;
  entry: TimestampedEntry;
  /** exact bytes, used for the leaf hash */
  raw: Uint8Array;
}

/** RFC 6962 §4.6 `extra_data`: for precerts the whole PrecertChainEntry, otherwise only the chain. */
export interface ExtraData {
  /** Full precertificate DER (precert entries only). */
  precert?: Uint8Array;
  /** Issuer chain, leaf's issuer first. For precerts this may start with a Precertificate Signing Certificate. */
  chain: Uint8Array[];
}

/** RFC 6962 §3.3 SignedTreeHead as returned by get-sth. */
export interface SignedTreeHead {
  treeSize: number;
  timestamp: number;
  rootHash: Uint8Array;
  signature: DigitallySigned;
}

/** RFC 6962 §3.2 SignedCertificateTimestamp. */
export interface Sct {
  version: number;
  logId: Uint8Array;
  timestamp: number;
  extensions: Uint8Array;
  signature: DigitallySigned;
  /** TLS-encoded SCT bytes */
  raw: Uint8Array;
}

/** What an SCT signs: the same `signed_entry` choice as TimestampedEntry. */
export type SignedEntry =
  | { type: 'x509'; cert: Uint8Array }
  | { type: 'precert'; issuerKeyHash: Uint8Array; tbs: Uint8Array };
